import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './VideoStatusPoller.css';

const VideoStatusPoller = ({ jobId, token, onUpdate }) => {
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Initializing...');
  const [pollingCount, setPollingCount] = useState(0);
  const intervalRef = useRef(null);

  const BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:10000'}/api/videos`;
  const POLL_INTERVAL = 5000; // 5 seconds
  const MAX_POLLS = 60; // Stop after 5 minutes (60 * 5 seconds)

  useEffect(() => {
    // Start polling
    checkStatus();
    intervalRef.current = setInterval(() => {
      checkStatus();
    }, POLL_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [jobId, token]);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const video = response.data;

      // Update progress bar (simulate progress based on status)
      if (video.status === 'pending') {
        setProgress(Math.min(20 + pollingCount * 2, 40));
        setStatusMessage('⏳ Waiting in queue...');
      } else if (video.status === 'processing') {
        setProgress(Math.min(40 + pollingCount * 3, 90));
        setStatusMessage('🎬 Generating video...');
      } else if (video.status === 'complete') {
        setProgress(100);
        setStatusMessage('✅ Video ready!');

        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        // Notify parent component
        if (onUpdate) {
          onUpdate(video);
        }
      } else if (video.status === 'failed') {
        setProgress(0);
        setStatusMessage('❌ Generation failed');

        // Stop polling
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        // Notify parent component
        if (onUpdate) {
          onUpdate(video);
        }
      }

      setPollingCount(prev => prev + 1);

      // Stop polling after max attempts
      if (pollingCount >= MAX_POLLS) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        setStatusMessage('⚠️ Polling timeout - please refresh');
      }

    } catch (error) {
      console.error('Error polling video status:', error);

      if (error.response?.status === 401) {
        setStatusMessage('❌ Session expired');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      } else if (error.response?.status === 404) {
        setStatusMessage('❌ Video job not found');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      }
    }
  };

  return (
    <div className="status-poller">
      <div className="status-message">{statusMessage}</div>
      <div className="progress-bar-container">
        <div
          className="progress-bar-fill"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      <div className="polling-info">
        <small>
          {progress < 100 && progress > 0
            ? `Checked ${pollingCount} times • Estimated: 2-3 minutes`
            : ''}
        </small>
      </div>
    </div>
  );
};

export default VideoStatusPoller;
